import { useState } from "react";
import {
  Dialog,
  Button,
  Box,
  Tabs,
  Tab,
  TextField,
  MenuItem,
  Alert,
  CircularProgress,
  Stack,
  Typography,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ImportExportIcon from "@mui/icons-material/ImportExport";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { HEADING_FONT } from "../theme/fonts";
import { COLORS, RADIUS, SHADOW } from "../theme/tokens";
import api from "../api";
import { useServers, useCategories } from "../hooks/queries";

function TabPanel({ children, value, index }) {
  if (value !== index) return null;
  return (
    <div role="tabpanel" id={`tabpanel-${index}`} aria-labelledby={`tab-${index}`}>
      {children}
    </div>
  );
}

// One numbered section inside a tab ("1. Download template", ...).
function Section({ step, title, hint, children }) {
  return (
    <Box
      sx={{
        p: 2.25,
        borderRadius: RADIUS.card,
        background: COLORS.surface,
        border: `1px solid ${COLORS.hairline}`,
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1.25} sx={{ mb: hint ? 0.75 : 1.75 }}>
        <Box
          sx={{
            width: 20,
            height: 20,
            borderRadius: "50%",
            flexShrink: 0,
            background: COLORS.primarySoft,
            color: COLORS.primaryDark,
            fontSize: 11,
            fontWeight: 700,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {step}
        </Box>
        <Typography
          sx={{
            fontSize: 11.5,
            fontWeight: 700,
            color: COLORS.ink,
            letterSpacing: "0.3px",
            textTransform: "uppercase",
          }}
        >
          {title}
        </Typography>
      </Stack>
      {hint && (
        <Typography sx={{ fontSize: 11.5, color: COLORS.muted, mb: 1.75, ml: 4 }}>
          {hint}
        </Typography>
      )}
      {children}
    </Box>
  );
}

// Dashed drop-zone style file picker. Shows the chosen file with a clear button.
function FilePicker({ label, file, onPick, onClear }) {
  return (
    <Box>
      <Typography sx={{ fontSize: 12, fontWeight: 600, color: COLORS.muted, mb: 0.75 }}>
        {label}
      </Typography>

      {file ? (
        <Stack
          direction="row"
          alignItems="center"
          spacing={1.25}
          sx={{
            px: 1.75,
            py: 1.25,
            borderRadius: RADIUS.input,
            border: `1px solid ${COLORS.primary}`,
            background: COLORS.primarySoft,
          }}
        >
          <CheckCircleIcon sx={{ fontSize: 18, color: COLORS.primary }} />
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              noWrap
              sx={{ fontSize: 13, fontWeight: 600, color: COLORS.ink }}
            >
              {file.name}
            </Typography>
            <Typography sx={{ fontSize: 11, color: COLORS.muted }}>
              {(file.size / 1024).toFixed(1)} KB
            </Typography>
          </Box>
          <IconButton size="small" onClick={onClear} aria-label={`Remove ${label}`}>
            <CloseIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Stack>
      ) : (
        <Box
          component="label"
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.25,
            px: 1.75,
            py: 1.5,
            cursor: "pointer",
            borderRadius: RADIUS.input,
            border: `1px dashed ${COLORS.hairlineStrong}`,
            background: COLORS.tile,
            transition: "all 0.15s ease",
            "&:hover": {
              borderColor: COLORS.primary,
              background: COLORS.primarySoft,
            },
          }}
        >
          <CloudUploadOutlinedIcon sx={{ fontSize: 19, color: COLORS.muted }} />
          <Typography sx={{ fontSize: 13, color: COLORS.muted }}>
            Choose a .csv file
          </Typography>
          <input type="file" accept=".csv" hidden onChange={onPick} />
        </Box>
      )}
    </Box>
  );
}

const selectSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: RADIUS.input,
    background: COLORS.surface,
    fontSize: 13.5,
    "& fieldset": { borderColor: COLORS.hairlineStrong },
    "&:hover fieldset": { borderColor: COLORS.borderHover },
    "&.Mui-focused fieldset": { borderColor: COLORS.primary },
  },
};

function ImportExportModal({ open, onClose, onSuccess }) {
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const { data: serversData } = useServers();
  const servers = Array.isArray(serversData)
    ? serversData
    : serversData?.rows || [];
  const { data: categoriesData } = useCategories();
  const categories = Array.isArray(categoriesData)
    ? categoriesData
    : categoriesData?.rows || [];

  // Import state
  const [importFiles, setImportFiles] = useState({
    domains: null,
    subdomains: null,
  });

  // Export state
  const [exportServerFilter, setExportServerFilter] = useState("");
  const [exportCategoryFilter, setExportCategoryFilter] = useState("");

  const setFile = (type, file) => {
    setImportFiles((prev) => ({ ...prev, [type]: file }));
    setMessage("");
    setError("");
  };

  const switchTab = (_e, val) => {
    setTab(val);
    setMessage("");
    setError("");
  };

  // Download a blank (header-only) CSV template from /public/templates
  const downloadTemplate = (type) => {
    const link = document.createElement("a");
    link.href = `/templates/${type}-import-template.csv`;
    link.download = `${type}-import-template.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleImport = async () => {
    if (!importFiles.domains && !importFiles.subdomains) {
      setError("Please upload a Domains and/or a Subdomains CSV file");
      return;
    }

    setLoading(true);
    setError("");
    setMessage("");

    try {
      const payload = {};
      if (importFiles.domains) {
        payload.domainsCSV = await importFiles.domains.text();
      }
      if (importFiles.subdomains) {
        payload.subdomainsCSV = await importFiles.subdomains.text();
      }

      const response = await api.post("/import/domains-subdomains", payload);

      const d = response.details || {};
      const skipped = (d.domainsSkipped || 0) + (d.subdomainsSkipped || 0);
      setMessage(
        `✓ ${response.message}` +
          (skipped ? ` — ${skipped} already existed and were skipped.` : "") +
          (d.errors ? `\n⚠ ${d.errors.join(", ")}` : "")
      );
      setImportFiles({ domains: null, subdomains: null });
      setTimeout(() => {
        onSuccess?.();
        onClose();
      }, 1500);
    } catch (err) {
      setError(err.message || "Import failed");
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    setLoading(true);
    setError("");
    setMessage("");

    try {
      const params = new URLSearchParams();
      if (exportServerFilter) params.append("serverName", exportServerFilter);
      if (exportCategoryFilter)
        params.append("categoryName", exportCategoryFilter);

      const response = await api.get(
        `/export/domains-subdomains?${params.toString()}`
      );

      const downloadCSV = (csv, filename) => {
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const link = document.createElement("a");
        const url = URL.createObjectURL(blob);
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      };

      const stamp = new Date().toISOString().split("T")[0];
      downloadCSV(response.domainsCSV, `domains-export-${stamp}.csv`);
      downloadCSV(response.subdomainsCSV, `subdomains-export-${stamp}.csv`);

      setMessage(
        `✓ Exported ${response.domainsCount} domains and ${response.subdomainsCount} subdomains`
      );
    } catch (err) {
      setError(err.message || "Export failed");
    } finally {
      setLoading(false);
    }
  };

  const canImport = Boolean(importFiles.domains || importFiles.subdomains);

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            borderRadius: RADIUS.card,
            background: "#fff",
            border: `1px solid ${COLORS.hairline}`,
            boxShadow: SHADOW.modal,
            backgroundImage: "none",
            overflow: "hidden",
          },
        },
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", maxHeight: "90vh" }}>
        {/* Blue gradient top header */}
        <Box
          sx={{
            position: "relative",
            display: "flex",
            alignItems: "center",
            gap: 2,
            px: { xs: 2.5, md: 3.5 },
            py: 2.5,
            color: "#fff",
            flexShrink: 0,
            overflow: "hidden",
            background: `linear-gradient(135deg, ${COLORS.primaryDark} 0%, ${COLORS.primary} 100%)`,
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: -50,
              right: -30,
              width: 160,
              height: 160,
              borderRadius: "50%",
              background: "rgba(255,255,255,0.12)",
              filter: "blur(40px)",
            }}
          />
          <Box
            sx={{
              width: 50,
              height: 50,
              borderRadius: "14px",
              background: "rgba(255,255,255,0.18)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              position: "relative",
              zIndex: 1,
            }}
          >
            <ImportExportIcon sx={{ fontSize: 26 }} />
          </Box>
          <Box sx={{ minWidth: 0, position: "relative", zIndex: 1 }}>
            <Typography
              sx={{
                fontSize: 10.5,
                fontWeight: 700,
                letterSpacing: "1.5px",
                textTransform: "uppercase",
                opacity: 0.85,
              }}
            >
              Domains &amp; Subdomains
            </Typography>
            <Typography
              sx={{
                fontFamily: HEADING_FONT,
                fontSize: 22,
                fontWeight: 700,
                letterSpacing: "-0.4px",
                lineHeight: 1.15,
              }}
            >
              Import / Export
            </Typography>
          </Box>

          <IconButton
            onClick={onClose}
            disabled={loading}
            aria-label="Close"
            sx={{
              ml: "auto",
              position: "relative",
              zIndex: 1,
              color: "#fff",
              background: "rgba(255,255,255,0.16)",
              "&:hover": { background: "rgba(255,255,255,0.28)" },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Tabs */}
        <Box
          sx={{
            flexShrink: 0,
            px: { xs: 1.5, md: 2.5 },
            background: COLORS.surface,
            borderBottom: `1px solid ${COLORS.hairline}`,
          }}
        >
          <Tabs
            value={tab}
            onChange={switchTab}
            sx={{
              minHeight: 44,
              "& .MuiTab-root": {
                minHeight: 44,
                px: 2,
                fontSize: 13,
                fontWeight: 600,
                textTransform: "none",
                color: COLORS.muted,
                "&.Mui-selected": { color: COLORS.primaryDark },
              },
              "& .MuiTabs-indicator": {
                height: 2.5,
                borderRadius: "2px 2px 0 0",
                background: COLORS.primary,
              },
            }}
          >
            <Tab
              icon={<CloudUploadOutlinedIcon sx={{ fontSize: 17 }} />}
              iconPosition="start"
              label="Import"
              id="tab-0"
              aria-controls="tabpanel-0"
            />
            <Tab
              icon={<DownloadOutlinedIcon sx={{ fontSize: 17 }} />}
              iconPosition="start"
              label="Export"
              id="tab-1"
              aria-controls="tabpanel-1"
            />
          </Tabs>
        </Box>

        {/* Body */}
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            p: { xs: 2, md: 2.5 },
            background: COLORS.canvas,
          }}
        >
          {error && (
            <Alert
              severity="error"
              sx={{ mb: 2, borderRadius: RADIUS.input, fontSize: 12.5 }}
            >
              {error}
            </Alert>
          )}
          {message && (
            <Alert
              severity="success"
              sx={{
                mb: 2,
                borderRadius: RADIUS.input,
                fontSize: 12.5,
                whiteSpace: "pre-line",
              }}
            >
              {message}
            </Alert>
          )}

          {/* Import */}
          <TabPanel value={tab} index={0}>
            <Stack spacing={2}>
              <Section
                step={1}
                title="Download a blank template"
                hint="Each template has the column headers only — fill in your rows in Excel or Sheets."
              >
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ ml: 4 }}>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<DescriptionOutlinedIcon />}
                    onClick={() => downloadTemplate("domains")}
                    sx={{
                      textTransform: "none",
                      borderRadius: RADIUS.input,
                      borderColor: COLORS.hairlineStrong,
                      color: COLORS.ink,
                      "&:hover": {
                        borderColor: COLORS.primary,
                        background: COLORS.primarySoft,
                      },
                    }}
                  >
                    Domains template
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<DescriptionOutlinedIcon />}
                    onClick={() => downloadTemplate("subdomains")}
                    sx={{
                      textTransform: "none",
                      borderRadius: RADIUS.input,
                      borderColor: COLORS.hairlineStrong,
                      color: COLORS.ink,
                      "&:hover": {
                        borderColor: COLORS.primary,
                        background: COLORS.primarySoft,
                      },
                    }}
                  >
                    Subdomains template
                  </Button>
                </Stack>
              </Section>

              <Section
                step={2}
                title="Upload your filled file(s)"
                hint="Upload one or both. Subdomains can be imported on their own, as long as their parent domains already exist."
              >
                <Stack spacing={1.75} sx={{ ml: 4 }}>
                  <FilePicker
                    label="Domains CSV"
                    file={importFiles.domains}
                    onPick={(e) =>
                      e.target.files[0] && setFile("domains", e.target.files[0])
                    }
                    onClear={() => setFile("domains", null)}
                  />
                  <FilePicker
                    label="Subdomains CSV"
                    file={importFiles.subdomains}
                    onPick={(e) =>
                      e.target.files[0] &&
                      setFile("subdomains", e.target.files[0])
                    }
                    onClear={() => setFile("subdomains", null)}
                  />
                </Stack>
              </Section>
            </Stack>
          </TabPanel>

          {/* Export */}
          <TabPanel value={tab} index={1}>
            <Stack spacing={2}>
              <Section
                step={1}
                title="Narrow the export (optional)"
                hint="Leave both as “All” to export everything."
              >
                <Stack spacing={1.75} sx={{ ml: 4 }}>
                  <Box>
                    <Typography
                      sx={{ fontSize: 12, fontWeight: 600, color: COLORS.muted, mb: 0.75 }}
                    >
                      Server
                    </Typography>
                    <TextField
                      select
                      fullWidth
                      size="small"
                      value={exportServerFilter}
                      onChange={(e) => setExportServerFilter(e.target.value)}
                      slotProps={{ select: { displayEmpty: true } }}
                      sx={selectSx}
                    >
                      <MenuItem value="">All servers</MenuItem>
                      {servers.map((s) => (
                        <MenuItem key={s.id} value={s.name}>
                          {s.name}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Box>

                  <Box>
                    <Typography
                      sx={{ fontSize: 12, fontWeight: 600, color: COLORS.muted, mb: 0.75 }}
                    >
                      Category
                    </Typography>
                    <TextField
                      select
                      fullWidth
                      size="small"
                      value={exportCategoryFilter}
                      onChange={(e) => setExportCategoryFilter(e.target.value)}
                      slotProps={{ select: { displayEmpty: true } }}
                      sx={selectSx}
                    >
                      <MenuItem value="">All categories</MenuItem>
                      {categories.map((c) => (
                        <MenuItem key={c.id} value={c.name}>
                          {c.name}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Box>
                </Stack>
              </Section>

              <Section step={2} title="Download">
                <Typography sx={{ fontSize: 12.5, color: COLORS.muted, ml: 4 }}>
                  Two CSV files will be downloaded — one for domains and one for
                  subdomains. Both use the same columns as the import templates,
                  so an exported file can be edited and imported straight back.
                </Typography>
              </Section>
            </Stack>
          </TabPanel>
        </Box>

        {/* Footer */}
        <Stack
          direction="row"
          justifyContent="flex-end"
          spacing={1.25}
          sx={{
            flexShrink: 0,
            px: { xs: 2, md: 2.5 },
            py: 2,
            background: COLORS.surface,
            borderTop: `1px solid ${COLORS.hairline}`,
          }}
        >
          <Button
            onClick={onClose}
            disabled={loading}
            sx={{
              textTransform: "none",
              borderRadius: RADIUS.input,
              px: 2.5,
              color: COLORS.muted,
              "&:hover": { background: COLORS.tile },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={tab === 0 ? handleImport : handleExport}
            disabled={loading || (tab === 0 && !canImport)}
            variant="contained"
            disableElevation
            startIcon={
              loading ? (
                <CircularProgress size={15} sx={{ color: "#fff" }} />
              ) : tab === 0 ? (
                <CloudUploadOutlinedIcon sx={{ fontSize: 18 }} />
              ) : (
                <DownloadOutlinedIcon sx={{ fontSize: 18 }} />
              )
            }
            sx={{
              textTransform: "none",
              fontWeight: 600,
              borderRadius: RADIUS.input,
              px: 3,
              background: `linear-gradient(135deg, ${COLORS.primaryDark} 0%, ${COLORS.primary} 100%)`,
              boxShadow: "0 8px 22px rgba(59, 130, 246, 0.35)",
              "&:hover": { boxShadow: "0 10px 26px rgba(59, 130, 246, 0.45)" },
              "&.Mui-disabled": {
                background: COLORS.hairlineStrong,
                color: "#fff",
                boxShadow: "none",
              },
            }}
          >
            {tab === 0
              ? loading
                ? "Importing…"
                : "Import"
              : loading
                ? "Exporting…"
                : "Export"}
          </Button>
        </Stack>
      </Box>
    </Dialog>
  );
}

export default ImportExportModal;
